import { useState } from 'react';
import {
  Callout,
  Facts,
  Field,
  Panel,
  TextInput,
  formatDate,
} from '@affdice/portal-kit';
import { PlatformApi } from '../../../client.ts';
import { t } from '../../../messages.ts';
import { useAbilities } from '../../abilities.tsx';
import { DirectEditDialog } from '../../DirectEditDialog.tsx';
import {
  COMPANY_FIELDS,
  companyChanges,
  companyDraft,
  companyProblem,
  type CompanyDraft,
  type CompanyField,
} from '../../direct-edit-model.ts';
import { OperatorSettingsPanel } from './OperatorSettings.tsx';

/**
 * Profile — who this operator is, and the operator-level settings. Its state,
 * and what can be done to it (suspend, reactivate, delete, restore, purge),
 * are the actions panel above every section, not here, so neither is shown
 * twice. A viewer holding the edit capability changes the company profile
 * and the settings directly (3.9d).
 */
type OperatorDetailData = Awaited<ReturnType<PlatformApi['operatorDetail']>>;

export function ProfileSection({
  api,
  operatorId,
  detail,
  owner,
  saved,
  onSaved,
}: {
  api: PlatformApi;
  operatorId: string;
  detail: OperatorDetailData;
  /** The admin contact, from the list row — the detail read does not carry it. */
  owner: { owner_name: string | null; owner_email: string | null; owner_phone: string | null } | null;
  /** Whether the company profile was just saved — held by the popup, which re-reads (and so remounts this) after a save. */
  saved: boolean;
  /** The company profile was saved: re-read the operator. */
  onSaved: () => void;
}) {
  const { can } = useAbilities();
  const [editing, setEditing] = useState(false);
  const o = detail.operator;
  const text = (k: string) => String(o[k] ?? '') || '—';
  const address = ['address_line', 'city', 'region', 'postal_code']
    .map((k) => String(o[k] ?? '').trim())
    .filter((v) => v !== '')
    .join(', ');

  return (
    <>
      <Panel
        title={t('ops.profile.title')}
        aside={can.edit ? (
          <button type="button" className="copy" onClick={() => setEditing(true)}>
            {t('edit.company.action')}
          </button>
        ) : null}
      >
        {saved ? <Callout fold={false}>{t('edit.company.done')}</Callout> : null}
        <Facts
          items={[
            { label: t('common.operator'), value: text('display_name') },
            { label: t('ops.list.legalName'), value: text('legal_name') },
            { label: t('edit.company.address'), value: address || '—' },
            { label: t('common.currency'), value: text('base_currency') },
            { label: t('common.country'), value: text('country') },
            { label: t('ops.list.joined'), value: o['created'] ? formatDate(String(o['created'])) : '—' },
            { label: t('ops.profile.wentLive'), value: o['went_live_at'] ? formatDate(String(o['went_live_at'])) : '—' },
            { label: t('ops.list.admin'), value: owner?.owner_name ?? '—' },
            { label: t('ops.profile.adminEmail'), value: owner?.owner_email ?? '—' },
            { label: t('ops.profile.adminPhone'), value: owner?.owner_phone ?? '—' },
          ]}
        />
      </Panel>

      <OperatorSettingsPanel api={api} operatorId={operatorId} />

      {editing ? (
        <CompanyEdit
          api={api}
          operatorId={operatorId}
          name={String(o['display_name'] ?? '')}
          original={companyDraft(o)}
          onClose={() => setEditing(false)}
          onDone={() => {
            setEditing(false);
            onSaved();
          }}
        />
      ) : null}
    </>
  );
}

const COMPANY_LABEL: Record<CompanyField, Parameters<typeof t>[0]> = {
  legal_name: 'ops.list.legalName',
  address_line: 'edit.company.addressLine',
  city: 'edit.company.city',
  region: 'edit.company.region',
  postal_code: 'edit.company.postalCode',
  country: 'common.country',
};

function CompanyEdit({
  api,
  operatorId,
  name,
  original,
  onClose,
  onDone,
}: {
  api: PlatformApi;
  operatorId: string;
  name: string;
  original: CompanyDraft;
  onClose: () => void;
  onDone: () => void;
}) {
  const [draft, setDraft] = useState<CompanyDraft>(original);
  const changes = companyChanges(original, draft);
  const problem = companyProblem(draft);
  return (
    <DirectEditDialog
      wide
      title={t('edit.company.title', { name })}
      submitLabel={t('edit.saveNow')}
      ready={changes !== null && problem === null}
      run={(reason) => api.setOperatorCompany(operatorId, changes ?? {}, reason)}
      onClose={onClose}
      onDone={onDone}
    >
      <div className="form-row">
        {COMPANY_FIELDS.map((f) => (
          <Field
            key={f}
            label={t(COMPANY_LABEL[f])}
            {...(f === 'legal_name' && problem === 'legal-name' ? { error: t('edit.company.legalNameRequired') } : {})}
            {...(f === 'country'
              ? problem === 'country' ? { error: t('edit.company.countryCode') } : { hint: t('edit.company.countryHint') }
              : {})}
          >
            <TextInput value={draft[f]} onChange={(v) => setDraft((d) => ({ ...d, [f]: v }))} />
          </Field>
        ))}
      </div>
    </DirectEditDialog>
  );
}
